import { IImageSelectorType } from "@/components/content-image-editor/content-image-editor";
import Loading from "@/components/loading/loading.vue";
import { ContentTitleModule } from "@/modules/content-title";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import UnitState from "@/modules/unit";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ContainerType } from "@/services/admin";
import { ContentCMImage, ContentService, ContentTitleModel } from "@/services/content";
import { VCSLevelUnitResponseModel } from "@/services/text-guardian/unit-list/model";
import { EmptyGuid } from "@/util/constants";
import { ContentCategoryEnum, CurriculumType } from "@/util/enum";
import { Form, Input, Select } from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { debounce, isUndefined, last, merge } from "lodash";
import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Action, ActionItem } from "commonui";
import { Annotation } from "@/models/image-meta-data";
import ToolBox from "@/components/annotations/toobox/toolbox-canvas.vue";
import { VCSLevelModel } from "@/models/title-list-model";

const UNIT_ALL = 0;
const CONTENT_ALL = "All";

@Component({
	components: {
		Search: Input.Search,
		Select,
		Option: Select.Option,
		Form,
		FormItem: Form.Item,
		Loading,
		Action,
		ActionItem,
		ToolBox
	}
})
export default class AssetImageContent extends Vue {
	@Prop({ default: "" }) versionGroupId!: string;
	@Prop({ default: "" }) contentId!: string;
	@Prop({ default: 0 }) unitId!: string | number;
	@Prop({ default: "" }) pageId!: string;
	@Prop({ default: false }) isAnnotationView!: boolean;
	@Prop({ default: () => [] }) units!: VCSLevelUnitResponseModel[];
	@Prop({ default: () => [] }) versionGroupIds!: string[];
	@Prop({ default: () => {} }) setAnnotationView!: () => void;

	@Ref() readonly selectorOpen!: boolean;
	@Ref() readonly strokeWidth!: number;
	@Ref() readonly hasStickerTool!: boolean;

	public form!: WrappedFormUtils;
	public loading: boolean = false;
	public images: ContentCMImage[] = [];
	public versionGroups = VersionGroupModule.versionGroups;
	private unitModule = getModule(UnitState);
	public titles: ContentTitleModel[] = [];
	public titlesLoading: boolean = false;
	public showVersionGroup: boolean = false;
	public canvas: any;
	public allUnits: VCSLevelUnitResponseModel[] = [];

	private conversationStore = getModule(ConversationBuilderState);
	private signatureBase!: string;
	private signatureParams!: string;
	private firstLoad = true;
	private formEmitEvent = true;
	private mediaId: string = "";

	created() {
		this.allUnits = this.units;
		this.conversationStore.getDownloadSasURL().then(this.setSasUrl);
		const debouncedValueChange = debounce(this.onValuesChange, 500);
		const onValuesChange = (props, fields) => {
			if (this.formEmitEvent) {
				if (fields.versionGroupId) {
					this.setDefaultUnitId();
				}
				if (!isUndefined(fields.versionGroupId) || !isUndefined(fields.unitId)) {
					this.setTitles(fields);
				}
			}
			this.formEmitEvent = true;

			return debouncedValueChange();
		};
		this.form = this.$form.createForm(this, {
			name: "image-editor-form",
			onValuesChange: onValuesChange.bind(this)
		});
	}

	async mounted() {
		let defaultVersionGroupId: string | undefined = "";

		const unit = this.units.find((unit) => unit.id == this.unitId);
		if (unit?.curriculumType === CurriculumType.GrapeSEED) {
			defaultVersionGroupId = this.versionGroups
				.filter((vg) => vg.curriculumType === CurriculumType.GrapeSEED)
				.find((vg) => this.versionGroupIds.includes(vg.id))?.id as string;
		} else {
			defaultVersionGroupId = this.versionGroups
				.filter((vg) => vg.curriculumType === CurriculumType.LittleSEED)
				.find((vg) => this.versionGroupIds.includes(vg.id))?.id as string;
		}

		const promises: Promise<any>[] = [this.conversationStore.getDownloadSasURL()];

		const versionGroupId = defaultVersionGroupId;
		promises.push(
			ContentTitleModule.getTitles({
				versionGroupId
			})
		);

		const [_, allTitles] = await Promise.all(promises);
		const isContentPresent = allTitles.find((f) => f.contentId === this.contentId);
		const unitId = isContentPresent ? allTitles.find((t) => t.contentId === this.contentId)?.unitId : null;

		if (this.pageId) {
			this.getPageImage(this.pageId, versionGroupId);
		} else {
			const contentId = isContentPresent
				? this.contentId
				: this.$route.params.contentId || this.$route.params.id || this.contentId || CONTENT_ALL;

			this.form.setFieldsValue({
				unitId: unitId || this.conversationStore.conversationExtraInfo?.unitId || UNIT_ALL,
				versionGroupId,
				keyword: null,
				contentId
			});
		}
	}

	private getPageImage(pageId: string, defaultVersionGroupId: string) {
		return ContentService.getPageImage(pageId).then((response) => {
			this.form.setFieldsValue({
				unitId: response.unitId,
				versionGroupId: defaultVersionGroupId,
				keyword: null,
				contentId: response.contentId
			});

			this.mediaId = response.mediaId;
			this.edit(response, true);
		});
	}

	private setSasUrl() {
		const pageSignature = this.conversationStore.downloadSASURL?.find(
			(signature) => signature.type === ContainerType.Page
		);
		if (pageSignature) {
			const splitedUrl = pageSignature.url.split("?");
			this.signatureBase = splitedUrl[0] + pageSignature.name + "/";
			this.signatureParams = "?" + splitedUrl[1];
		}
	}

	public getImageUrl(path: string) {
		return this.signatureBase + path + this.signatureParams;
	}

	public onValuesChange() {
		this.form.validateFields((err, values) => {
			const { versionGroupId, keyword, unitId, contentId } = values;
			const hasContentId = !!contentId && contentId !== CONTENT_ALL;
			if (err || (!hasContentId && !keyword)) {
				return null;
			}
			this.loading = true;
			this.images = [];
			const componentName = this.titles.find((title) => title.contentId === contentId)?.title;

			ContentService.getImages(
				versionGroupId,
				keyword,
				unitId,
				contentId === CONTENT_ALL ? null : contentId,
				ContentCategoryEnum.CM
			)
				.then((images) => {
					this.images = images;
					if (this.firstLoad) {
						this.firstLoad = false;

						const image = images.find((i) => i.imageDetail.pageId === this.pageId);
						if (image) {
							// this.edit(image, true);
						}
					}

					if (images.length) {
						this.mediaId = images[0].mediaId;
					}
					const mediaId = this.mediaId;
					this.$emit("unitOrComponentChange", { unitId, componentName, mediaId });
					this.loading = false;
				})
				.catch(() => {
					this.loading = false;
				});
		});
	}

	private setTitles(fields: any) {
		const { versionGroupId, unitId, keyword, contentId } = merge(this.form.getFieldsValue(), fields);
		this.titlesLoading = true;
		let newVersionGroupId = versionGroupId;

		// fetch titles on the basis of the unit licensetype selected.
		const unit = this.units.find((unit) => unit.id === unitId);
		if (unit?.curriculumType === CurriculumType.GrapeSEED) {
			newVersionGroupId = this.versionGroups
				.filter((vg) => vg.curriculumType === CurriculumType.GrapeSEED)
				.find((vg) => this.versionGroupIds.includes(vg.id))?.id as string;
		} else {
			newVersionGroupId = this.versionGroups
				.filter((vg) => vg.curriculumType === CurriculumType.LittleSEED)
				.find((vg) => this.versionGroupIds.includes(vg.id))?.id as string;
		}

		if (newVersionGroupId && versionGroupId !== newVersionGroupId) {
			this.form.setFieldsValue({ versionGroupId: newVersionGroupId, unitId, keyword, contentId });
		}

		ContentTitleModule.getTitles({
			versionGroupId: newVersionGroupId,
			unitId
		}).then(
			(titles) => {
				this.titlesLoading = false;
				if (unitId === UNIT_ALL) {
					this.titles = [];
				} else {
					this.titles = titles;
				}
				this.setDefaultContentId();
			},
			() => {
				this.titlesLoading = false;
			}
		);
	}

	private setDefaultContentId(): void {
		const contentId = this.form.getFieldValue("contentId");
		if (contentId) {
			const hasContentTitle = this.titles.find((title) => title.contentId === contentId);
			if (!hasContentTitle) {
				this.formEmitEvent = false;
				this.form.setFieldsValue({ contentId: CONTENT_ALL });
			}
		}
	}
	private setDefaultUnitId(): void {
		const unitId = this.form.getFieldValue("unitId");
		if (unitId) {
			const hasUnitId = this.units.find((unit) => unit.id === unitId);
			if (!hasUnitId) {
				this.formEmitEvent = false;
				this.form.setFieldsValue({ unitId: 0 });
			}
		}
	}

	public edit(item: ContentCMImage, applyMetaData: boolean = false) {
		const fileName = last(item.imageUrl.split("/"));
		this.$emit("edit", this.getImageUrl(item.imageUrl), fileName, "image/png", item.imageDetail, applyMetaData);
	}

	public titleSearch(value: string) {
		const { unitId, versionGroupId } = this.form.getFieldsValue();
		if (!value?.length && unitId !== UNIT_ALL) {
			ContentTitleModule.getTitles({
				versionGroupId,
				unitId
			}).then((titles) => {
				this.titles = titles;
			});
			return;
		}
		if (value?.length < 2) {
			return;
		}

		ContentTitleModule.getTitles({
			versionGroupId
		}).then((titles) => {
			this.titles = titles.filter((title) => {
				if (unitId && title.unitId !== unitId) {
					return false;
				}
				return title.title.toLowerCase().indexOf(value) >= 0;
			});
		});
	}

	get isAnnotationOpen() {
		return this.isAnnotationView;
	}

	toggleShapeDrawer() {
		this.setAnnotationView();
	}

	public dropAnnotation(annotation: Partial<Annotation>) {
		this.$emit("addShape", annotation);
	}
}
